import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Typography } from '@mui/material';

import CancelledPlacementDetailDrawer from '../../../../main-deprecated/components/RosterWidget/CancelledPlacementDetailDrawer';
import DeletePlacementDrawer from '../../../../main-deprecated/views/jobs/jobList/detail/SubComponents/DeletePlacementDrawer';
import ManageUserDrawer from '../../../../main-deprecated/views/jobs/jobList/detail/SubComponents/ManageUserDrawer';
import BackdropCircularProgress from '../../../components/BackdropCircularProgress';
import WaeButton, { BUTTON_VARIANT } from '../../../components/Button';
import RecommendWorkerModal from '../../../components/RecommendWorkerModal/RecommendWorkerModal';
import selectUser from '../../../store/selectors/appSelector';
import { PRIMARY_COLOR } from '../../../theme/colorConstants';
import { epochToDateInReadableFormat, epochToDayOfWeek } from '../../../utils';
import { getUniqObjectsByKey } from '../../../utils/jobOrderHelpers';
import { ROUTE_CONSTANTS, searchParamOptions } from '../../app/RouteBuilding/routeConstants';
import { pendingJobKeys } from '../constants';
import {
  approveOrRejectEgApplicationV2,
  approveOrRejectPlacementV2,
  cancelPlacementV2,
  clearCancelledPlacementData,
  getCancelledPlacementDetail,
  getJobOrderDetailV2,
  inviteEmployeeV2,
  removeUserFromGroupV2,
  revokeJobInvitationsV2,
  revokeJobOfferV2,
  updateCancelledPlacementV2,
  updateJobRosterEmployeeV2,
  updatePlacementDepartmentV2,
} from '../jobOrderDetailReducer';
import {
  selectCancelledPlacement,
  selectIsCancelledPlacementLoading,
  selectIsRosterActionProcessingV2,
} from '../jobOrderDetailSelectors';

import ActiveEmployeeRosterCard from './EmployeeRosterCards/ActiveEmployeeRosterCard';
import CancelledEmployeeRosterCard from './EmployeeRosterCards/CancelledEmployeeRosterCard';
import EmptyEmployeeRosterCard from './EmployeeRosterCards/EmptyEmployeeRosterCard';
import PendingEmployeeRosterCard from './EmployeeRosterCards/PendingEmployeeRosterCard';
import ReservedEmployeeRosterCard from './EmployeeRosterCards/ReservedEmployeeRosterCard';
import RosterBlock from './RosterBlock';

const RosterPanel = ({ jobOrder }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const cancelledPlacement = useSelector(selectCancelledPlacement);
  const isCancelledPlacementLoading = useSelector(selectIsCancelledPlacementLoading);
  const isRosterActionProcessing = useSelector(selectIsRosterActionProcessingV2);
  const role = user?.role;
  const { numOpenings, numFilled } = jobOrder;
  const inviteNumber = Math.max(numOpenings - numFilled, 0);
  const activeEmployees =
    (jobOrder?.roster?.active || [])?.slice(0, Math.min(numOpenings, numFilled)) || [];

  const reserveList =
    numFilled > numOpenings ? (jobOrder?.roster?.active || []).slice(numOpenings) : [];
  const corporationId = jobOrder?.corpId;
  const [selectedPlacementToManage, setSelectedPlacementToManage] = useState(null);
  const [isUserManageDrawerOpen, setIsUserManageDrawerOpen] = useState(false);
  const [selectedPlacementToCancel, setSelectedPlacementToCancel] = useState(null);
  const [isDeletePlacementDrawerOpen, setIsDeletePlacementDrawerOpen] = useState(false);
  const [isRecommendedWorkerModalOpen, setIsRecommendedWorkerModalOpen] = useState(false);

  const handleRecommendWorkerSuccess = useCallback(() => {
    // Refresh job detail via Redux instead of full page reload
    if (jobOrder?.id) {
      dispatch(getJobOrderDetailV2({ jobId: jobOrder.id }));
    }
    setIsRecommendedWorkerModalOpen(false);
  }, [dispatch, jobOrder?.id]);

  useEffect(() => {
    if (selectedPlacementToManage !== null) {
      setIsUserManageDrawerOpen(true);
    }
    if (selectedPlacementToCancel !== null) {
      setIsDeletePlacementDrawerOpen(true);
    }
  }, [selectedPlacementToManage, selectedPlacementToCancel]);

  const handleCandidateUpdate = useCallback(
    (employeeData) => {
      dispatch(updateJobRosterEmployeeV2(employeeData));
    },
    [dispatch]
  );

  const handleCancelPlacement = useCallback(
    (placementId, skipNotifications) => {
      dispatch(
        cancelPlacementV2({ id: placementId, ...(skipNotifications && { skipNotifications }) })
      );
    },
    [dispatch]
  );

  const handleCancelPlacementEntireGroup = useCallback(
    (placement, skipNotifications) => {
      dispatch(
        removeUserFromGroupV2({
          candidate: placement?.employeeId,
          extensionGroup: jobOrder?.extensionGroup?.id,
          ...(skipNotifications && { skipNotifications }),
        })
      );
    },
    [dispatch, jobOrder?.extensionGroup?.id]
  );

  const handleOnCloseUserManageDrawer = useCallback(() => {
    setSelectedPlacementToManage(null);
    setIsUserManageDrawerOpen(false);
  }, []);

  const handleOnCloseDeletePlacement = useCallback(() => {
    setSelectedPlacementToCancel(null);
    setIsDeletePlacementDrawerOpen(false);
  }, []);

  const handleRevokeInvite = useCallback(
    (placementId, isOffer) => {
      if (isOffer) {
        dispatch(
          revokeJobOfferV2({
            extensionGroup: jobOrder?.extensionGroup?.id,
            candidateId: placementId,
            accepted: false,
          })
        );
      } else {
        dispatch(revokeJobInvitationsV2({ placement: placementId }));
      }
    },
    [dispatch, jobOrder?.extensionGroup?.id]
  );

  const handleResendInvite = useCallback(
    (candidateId) => {
      dispatch(
        inviteEmployeeV2({
          jobOrder: jobOrder.id,
          candidates: [candidateId],
        })
      );
    },
    [dispatch, jobOrder]
  );

  const handleAcceptOrRejectInvite = useCallback(
    (placementId, isApproved) => {
      dispatch(
        approveOrRejectPlacementV2({
          fields: {
            placement: placementId,
            approved: isApproved,
          },
        })
      );
    },
    [dispatch]
  );

  const handleAcceptOrRejectEgApplication = useCallback(
    (egId, isApproved, employeeId) => {
      dispatch(
        approveOrRejectEgApplicationV2({
          extensionGroup: egId,
          candidate: employeeId,
          accepted: isApproved,
        })
      );
    },
    [dispatch]
  );

  const handleOnCancelledPlacementClick = useCallback(
    (placement) => {
      const payload = {
        filters: [
          {
            operation: 'equalsID',
            field: '_id',
            value: placement.id,
          },
        ],
      };
      dispatch(getCancelledPlacementDetail(payload));
    },
    [dispatch]
  );

  const getViewTimePath = (placement) => {
    const queryParams = new URLSearchParams({
      [searchParamOptions.START_DATE]: jobOrder?.start,
      [searchParamOptions.END_DATE]: jobOrder?.end,
      [searchParamOptions.EMPLOYEE]: placement.employeeId,
    });
    return `/${ROUTE_CONSTANTS.TIMEKEEPING}?${queryParams}`;
  };

  const handleFavoriteToggle = useCallback(
    (placement) => {
      const placementId = placement?.id ?? placement?.placementId ?? placement?._id;
      if (!placementId) return;
      dispatch(
        updateJobRosterEmployeeV2({
          id: placementId,
          favorite: !placement.isFavorite,
        })
      );
    },
    [dispatch]
  );

  const handleDepartmentUpdate = useCallback(
    (placementId, department) => {
      const payload = {
        placementId,
        departments: [
          {
            departmentId: department._id,
            allocatedTime: 0,
            isAllocatedRemainingTime: true,
          },
        ],
      };
      dispatch(updatePlacementDepartmentV2(payload));
    },
    [dispatch]
  );

  const activeEmployeesBlock = (
    <>
      {activeEmployees?.map((placement) => (
        <ActiveEmployeeRosterCard
          key={placement.id}
          placement={placement}
          isJobInFuture={jobOrder?.start > Date.now()}
          isOngoingStatus={jobOrder?.extensionGroup && placement.isOnAutoPlacementList}
          onManageCandidate={() => setSelectedPlacementToManage(placement)}
          onDeleteFromOne={() => setSelectedPlacementToCancel({ ...placement, isOneOpening: true })}
          onDeleteFromAll={() =>
            setSelectedPlacementToCancel({ ...placement, isOneOpening: false })
          }
          viewTimePath={getViewTimePath(placement)}
          extensionGroupId={jobOrder?.extensionGroup?.id}
          corporationId={corporationId}
          onDepartmentUpdate={handleDepartmentUpdate}
          onFavoriteClick={() => handleFavoriteToggle(placement)}
        />
      ))}
      {Array.from({ length: inviteNumber }, (_, index) => index).map((number) => (
        <EmptyEmployeeRosterCard key={number} />
      ))}
    </>
  );

  const reservedEmployeesBlock = reserveList?.map((placement) => (
    <ReservedEmployeeRosterCard
      key={placement.id}
      placement={placement}
      isJobInFuture={jobOrder?.start > Date.now()}
      onManageCandidate={() => setSelectedPlacementToManage(placement)}
      onDeleteFromOne={() => setSelectedPlacementToCancel({ ...placement, isOneOpening: true })}
      onDeleteFromAll={() => setSelectedPlacementToCancel({ ...placement, isOneOpening: false })}
      viewTimePath={getViewTimePath(placement)}
      extensionGroupId={jobOrder?.extensionGroup?.id}
      corporationId={corporationId}
      onDepartmentUpdate={handleDepartmentUpdate}
      onFavoriteClick={() => handleFavoriteToggle(placement)}
    />
  ));

  const extGroupOfferedCandidates = jobOrder?.extensionGroup?.offeredCandidates || [];
  const invites = jobOrder?.roster?.invite || [];
  const applications = jobOrder?.roster?.application || [];
  const extGroupApplicantList = jobOrder?.extensionGroup?.applicantList || [];

  // Deduplicate by placement object keys (not React elements)
  const getPlacementObjKey = (placement, index) =>
    placement?.id ?? placement?.placementId ?? placement?._id ?? placement?.employeeId ?? index;

  const pendingInviteOfferEntries = [
    ...invites.map((placement) => ({ placement, kind: pendingJobKeys.INV })),
    ...extGroupOfferedCandidates.map((placement) => ({ placement, kind: pendingJobKeys.OFFER })),
  ];
  const pendingInvitesAndOffers = getUniqObjectsByKey(pendingInviteOfferEntries, (entry, index) =>
    getPlacementObjKey(entry?.placement, index)
  );

  const pendingApplicationEntries = [
    ...applications.map((placement) => ({ placement, kind: 'rosterApplication' })),
    ...extGroupApplicantList.map((placement) => ({ placement, kind: 'extGroupApplication' })),
  ];
  const pendingApplicationsAndApplicantList = getUniqObjectsByKey(
    pendingApplicationEntries,
    (entry, index) => getPlacementObjKey(entry?.placement, index)
  );

  const pendingInvitesAndOffersBlock = pendingInvitesAndOffers.map((entry, index) => {
    const placement = entry?.placement;
    const uniqueKey = getPlacementObjKey(placement, index);
    const candidateId = placement?.employeeId ?? placement?.id;

    if (entry?.kind === pendingJobKeys.OFFER) {
      return (
        <PendingEmployeeRosterCard
          key={uniqueKey}
          placement={placement}
          onManageCandidate={() => setSelectedPlacementToManage(placement)}
          onRevokeOrAcceptInvite={() => handleRevokeInvite(placement?.id, true)}
          onResendOrRejectInvite={() => handleResendInvite(candidateId)}
          status={pendingJobKeys.OFFER}
          onFavoriteClick={() => handleFavoriteToggle(placement)}
        />
      );
    }

    return (
      <PendingEmployeeRosterCard
        key={uniqueKey}
        placement={placement}
        onManageCandidate={() => setSelectedPlacementToManage(placement)}
        onRevokeOrAcceptInvite={() => handleRevokeInvite(placement?.id)}
        onResendOrRejectInvite={() => handleResendInvite(candidateId)}
        status={pendingJobKeys.INV}
        onFavoriteClick={() => handleFavoriteToggle(placement)}
      />
    );
  });

  const pendingApplicationsBlock = pendingApplicationsAndApplicantList.map((entry, index) => {
    const placement = entry?.placement;
    const uniqueKey = getPlacementObjKey(placement, index);

    if (entry?.kind === 'extGroupApplication') {
      return (
        <PendingEmployeeRosterCard
          key={uniqueKey}
          placement={placement}
          status={pendingJobKeys.APPLICATION}
          onManageCandidate={() => setSelectedPlacementToManage(placement)}
          onRevokeOrAcceptInvite={() =>
            handleAcceptOrRejectEgApplication(
              jobOrder?.extensionGroup?.id,
              true,
              placement?.employeeId
            )
          }
          onResendOrRejectInvite={() =>
            handleAcceptOrRejectEgApplication(
              jobOrder?.extensionGroup?.id,
              false,
              placement?.employeeId
            )
          }
          onFavoriteClick={() => handleFavoriteToggle(placement)}
        />
      );
    }

    return (
      <PendingEmployeeRosterCard
        key={uniqueKey}
        placement={placement}
        status={pendingJobKeys.APPLICATION}
        onManageCandidate={() => setSelectedPlacementToManage(placement)}
        onRevokeOrAcceptInvite={() => handleAcceptOrRejectInvite(placement?.id, true)}
        onResendOrRejectInvite={() => handleAcceptOrRejectInvite(placement?.id, false)}
        onFavoriteClick={() => handleFavoriteToggle(placement)}
      />
    );
  });

  const pendingEmployeesBlock = [...pendingInvitesAndOffersBlock, ...pendingApplicationsBlock];

  const cancelledEmployeesBlock = [
    ...(jobOrder?.roster?.cancelled || []),
    ...(jobOrder?.roster?.deniedBySystem || []),
  ]?.map((placement) => (
    <CancelledEmployeeRosterCard
      key={placement.id}
      placement={placement}
      onManageCandidate={() => setSelectedPlacementToManage(placement)}
      handleOnCancelledPlacementClick={handleOnCancelledPlacementClick}
      onFavoriteClick={() => handleFavoriteToggle(placement)}
    />
  ));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '1200px' }}>
      {isRosterActionProcessing && <BackdropCircularProgress />}
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            marginBottom: '48px',
          }}
        >
          <Typography sx={{ fontSize: 24, fontWeight: '700' }}>
            {epochToDayOfWeek(jobOrder.start)}
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: '400' }}>
            {epochToDateInReadableFormat(jobOrder.start)}
          </Typography>
        </Box>

        <WaeButton
          variant={BUTTON_VARIANT.DEFAULT}
          actionColor={PRIMARY_COLOR[70]}
          onClick={() => {
            setIsRecommendedWorkerModalOpen((prev) => !prev);
          }}
        >
          {user?.role === UserRole.ADMIN
            ? LanguageConverterV2('buttonText.addOrInviteEmployees')
            : LanguageConverterV2('buttonText.inviteEmployees')}
        </WaeButton>
      </Box>
      <Box sx={{ flexDirection: 'column' }}>
        <RosterBlock
          key="roster"
          testId="active"
          rosterContent={activeEmployeesBlock}
          title={`${LanguageConverterV2('jobOrder.roster')} (${
            activeEmployees.length
          }/${numOpenings})`}
          fillRatio={jobOrder?.fillRatio}
          inviteNumber={inviteNumber}
        />
        {reservedEmployeesBlock?.length > 0 && (
          <RosterBlock
            key="reserveList"
            testId="reserve"
            rosterContent={reservedEmployeesBlock}
            title={`${LanguageConverterV2('jobOrder.status.reserveList')} (${
              reservedEmployeesBlock.length
            })`}
          />
        )}
        {pendingEmployeesBlock?.length > 0 && (
          <RosterBlock
            key="pending"
            testId="pending"
            rosterContent={pendingEmployeesBlock}
            title={`${LanguageConverterV2('jobOrder.status.pending')} (${
              pendingEmployeesBlock.length
            })`}
          />
        )}
        {cancelledEmployeesBlock?.length > 0 && (
          <RosterBlock
            key="cancelled"
            testId="cancelled"
            rosterContent={cancelledEmployeesBlock}
            title={`${LanguageConverterV2('jobOrder.status.cancelled')} (${
              cancelledEmployeesBlock.length
            })`}
          />
        )}
      </Box>
      {selectedPlacementToManage && (
        <ManageUserDrawer
          candidateName={selectedPlacementToManage.employeeName}
          originalEmployerNote={selectedPlacementToManage?.employerNote}
          handleOnSave={handleCandidateUpdate}
          fav={selectedPlacementToManage?.isFavorite}
          placementId={selectedPlacementToManage?.id}
          isOpen={isUserManageDrawerOpen}
          onClose={handleOnCloseUserManageDrawer}
        />
      )}
      {selectedPlacementToCancel && (
        <DeletePlacementDrawer
          type="outlined"
          isOpen={isDeletePlacementDrawerOpen}
          onClose={handleOnCloseDeletePlacement}
          candidateName={selectedPlacementToCancel.employeeName}
          handleCancelPlacement={(skipNotifications) =>
            !jobOrder?.extensionGroup?.id || selectedPlacementToCancel?.isOneOpening
              ? handleCancelPlacement(selectedPlacementToCancel.id, skipNotifications)
              : handleCancelPlacementEntireGroup(selectedPlacementToCancel, skipNotifications)
          }
          isOneOpening={selectedPlacementToCancel.isOneOpening}
          isAdmin={role === UserRole.ADMIN}
        />
      )}
      {isRecommendedWorkerModalOpen && (
        <RecommendWorkerModal
          isOpen={isRecommendedWorkerModalOpen}
          onClose={() => setIsRecommendedWorkerModalOpen(false)}
          jobOrder={{ ...jobOrder, _id: jobOrder.id }}
          onSuccess={handleRecommendWorkerSuccess}
        />
      )}
      {cancelledPlacement?._id && (
        <CancelledPlacementDetailDrawer
          isOpen={cancelledPlacement?._id}
          cancelledPlacement={cancelledPlacement}
          onClose={() => dispatch(clearCancelledPlacementData())}
          handleUpdate={(val) => dispatch(updateCancelledPlacementV2(val))}
          isCancelledPlacementUpdating={isCancelledPlacementLoading}
        />
      )}
    </Box>
  );
};

RosterPanel.propTypes = {
  jobOrder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    start: PropTypes.number,
    end: PropTypes.number,
    numOpenings: PropTypes.number,
    numFilled: PropTypes.number,
    fillRatio: PropTypes.number,
    corpId: PropTypes.string,
    extensionGroup: PropTypes.shape({
      offeredCandidates: PropTypes.arrayOf({
        id: PropTypes.string,
        name: PropTypes.string,
        employeeName: PropTypes.string,
        employeePhoneNumber: PropTypes.string,
        employeeProfilePicture: PropTypes.string,
        employerNote: PropTypes.string,
        isInvite: PropTypes.bool,
        isFavorite: PropTypes.bool,
        reliabilityScore: PropTypes.number,
        rawReliability: PropTypes.number,
        rawAls: PropTypes.number,
        placementId: PropTypes.string,
      }),
      applicantList: PropTypes.arrayOf({
        id: PropTypes.string,
        name: PropTypes.string,
        employeeName: PropTypes.string,
        employeePhoneNumber: PropTypes.string,
        employeeProfilePicture: PropTypes.string,
        employerNote: PropTypes.string,
        isInvite: PropTypes.bool,
        isFavorite: PropTypes.bool,
        reliabilityScore: PropTypes.number,
        rawReliability: PropTypes.number,
        rawAls: PropTypes.number,
        placementId: PropTypes.string,
      }),
      id: PropTypes.string,
    }),
    roster: PropTypes.shape({
      active: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          employeeName: PropTypes.string,
          employeePhoneNumber: PropTypes.string,
          employeeProfilePicture: PropTypes.string,
          employerNote: PropTypes.string,
          isFavorite: PropTypes.bool,
          reliabilityScore: PropTypes.number,
          rawReliability: PropTypes.number,
          rawAls: PropTypes.number,
          isOnAutoPlacementList: PropTypes.string,
          placementId: PropTypes.string,
        })
      ),
      cancelled: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          employeeName: PropTypes.string,
          employeePhoneNumber: PropTypes.string,
          employeeProfilePicture: PropTypes.string,
          employerNote: PropTypes.string,
          isFavorite: PropTypes.bool,
          cancelledDate: PropTypes.number,
          cancelledWithin24Hours: PropTypes.bool,
          reliabilityScore: PropTypes.number,
          rawReliability: PropTypes.number,
          rawAls: PropTypes.number,
        })
      ),
      deniedBySystem: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          employeeName: PropTypes.string,
          employeePhoneNumber: PropTypes.string,
          employeeProfilePicture: PropTypes.string,
          employerNote: PropTypes.string,
          isFavorite: PropTypes.bool,
          cancelledDate: PropTypes.number,
          cancelledWithin24Hours: PropTypes.bool,
          reliabilityScore: PropTypes.number,
          rawReliability: PropTypes.number,
          rawAls: PropTypes.number,
        })
      ),
      invite: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          employeeName: PropTypes.string,
          employeePhoneNumber: PropTypes.string,
          employeeProfilePicture: PropTypes.string,
          employerNote: PropTypes.string,
          isInvite: PropTypes.bool,
          isFavorite: PropTypes.bool,
          reliabilityScore: PropTypes.number,
          rawReliability: PropTypes.number,
          rawAls: PropTypes.number,
          placementId: PropTypes.string,
        })
      ),
      application: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          employeeName: PropTypes.string,
          employeePhoneNumber: PropTypes.string,
          employeeProfilePicture: PropTypes.string,
          employerNote: PropTypes.string,
          isInvite: PropTypes.bool,
          isFavorite: PropTypes.bool,
          reliabilityScore: PropTypes.number,
          rawReliability: PropTypes.number,
          rawAls: PropTypes.number,
          placementId: PropTypes.string,
        })
      ),
    }),
  }),
};

export default RosterPanel;
